// Sentry filtering helpers for client and server SDKs
// Keep functions small, pure, and easily testable.

import type { Event as SentryEvent, EventHint as SentryEventHint } from "@sentry/core";

/**
 * Returns true if the event is a console-captured "Failed to fetch" error triggered by @logtail/next flush during navigation.
 */
export function shouldDropLogtailFailedToFetch({
  event,
  hint,
}: {
  event: SentryEvent;
  hint?: SentryEventHint;
}): boolean {
  const isConsoleLogger = event.logger === "console";
  const hasConsoleMechanism = !!event.exception?.values?.some(
    (v) => v.mechanism?.type === "console",
  );

  const original = hint?.originalException as unknown;
  const originalMessage =
    original &&
    typeof original === "object" &&
    original !== null &&
    "message" in (original as Record<string, unknown>) &&
    typeof (original as { message?: unknown }).message === "string"
      ? ((original as { message: string }).message as string)
      : undefined;

  const isFailedToFetch =
    originalMessage === "Failed to fetch" ||
    !!event.exception?.values?.some(
      (v) => typeof v.value === "string" && v.value.includes("Failed to fetch"),
    );

  const stackHasLogtail = !!event.exception?.values?.some((v) =>
    v.stacktrace?.frames?.some((f) => {
      const filename = typeof f.filename === "string" ? f.filename : "";
      const functionName = typeof f.function === "string" ? f.function : "";
      return (
        filename.includes("/@logtail/next/") ||
        filename.includes("node_modules/@logtail/next") ||
        functionName.toLowerCase().includes("logtail")
      );
    }),
  );

  return (isConsoleLogger || hasConsoleMechanism) && isFailedToFetch && stackHasLogtail;
}

/**
 * Returns true if the event corresponds to a handled microphone permission denial
 * (NotAllowedError/SecurityError) coming from getUserMedia. We mark these as benign
 * because the UI handles them and instructs the user to enable the microphone.
 */
export function shouldDropHandledMicPermissionError({ event }: { event: SentryEvent }): boolean {
  const hasUnhandledRejection = event.exception?.values?.some(
    (v) => v.mechanism?.type === "onunhandledrejection",
  );

  // Only consider unhandledrejection-tagged events
  if (!hasUnhandledRejection) return false;

  const isPermissionError = event.exception?.values?.some((v) => {
    const type = (v.type || "").toString();
    const value = (v.value || "").toString();
    return (
      type.includes("NotAllowedError") ||
      type.includes("SecurityError") ||
      value.includes("NotAllowedError") ||
      value.includes("Permission denied")
    );
  });

  // Heuristic: reference to getUserMedia in stack or value
  const mentionsGetUserMedia = event.exception?.values?.some((v) => {
    const value = (v.value || "").toString();
    const inFrames = !!v.stacktrace?.frames?.some(
      (f) => typeof f.function === "string" && f.function.toLowerCase().includes("getusermedia"),
    );
    return value.toLowerCase().includes("getusermedia") || inFrames;
  });

  return !!isPermissionError && !!mentionsGetUserMedia;
}
